<template>
  <div class="admin-container">
    <div class="admin-header">
      <h1>Sub Categories</h1>
    </div>
    <div class="border-container-unique37">
      <div class="admin-list-title">List of Sub Categories</div>
      <div>
        You can add, edit, and delete all of Sub Categories from this page.
      </div>

      <div class="admin-flex-container" ref="adminFlexContainer">
        <div class="admin-search-container">
          <input
            type="text"
            class="admin-search-input"
            placeholder="Search..."
            v-model="searchQuery"
            @input="handleSearchChange"
          />
          <img
            src="/images/maker-cart/union.svg"
            alt="Search"
            class="admin-search-icon"
          />
        </div>
        <div class="d-flex ga-3">
          <div>
            <BaseInput
              type="select"
              placeholder="Sort By"
              v-model="currentSort"
              :options="sortOptions"
              @update:modelValue="handleSortChange"
              :showSearch="false"
            />
          </div>
          <BaseButton
            variant="secondary"
            class="mx-2 my-md-auto"
            @click="handleAction({ type: 'add' })"
          >
            <div class="d-flex align-center ga-1">
              <img
                src="/images/icon/plus-circle.svg"
                width="20"
                height="20"
                alt="download-icon"
              />
              <span class="admin-button-text">Add Sub Categories</span>
            </div>
          </BaseButton>
        </div>
      </div>
      <div class="admin-list-tool-container"></div>

      <div style="margin: 10px 0px">
        <a
          href="/admin/categories"
          style="color: #e00069; cursor: pointer; text-decoration: none"
          >Categories /
        </a>
        <span style="color: #555555">{{ category.title }}</span>
      </div>

      <TableData
        :headers="headers"
        :items="tableItems"
        :loading="tableLoading"
        :total-pages="data?.data?.total_pages || 0"
        :current-page="currentPage"
        @update:current-page="changePage"
        @sort="handleSort"
        :use-api-sort="true"
        :is-action="true"
        :is-edit-action="true"
        :is-delete-action="true"
        @action="handleAction"
        variant="rounded"
      />
    </div>
  </div>

  <ConfirmationModal
    v-model="showModalDelete"
    variant="danger"
    title="Remove Sub Category"
    message="Are you sure want to remove this sub category?"
    confirm-text="Confirm"
    cancel-text="Back"
    :loading="isLoading"
    @confirm="remove(itemId)"
    @cancel="updateVisibilityModalDelete"
  />
</template>

<script setup>
/* eslint-disable */
import { onMounted, ref, computed, inject, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import TermAdminService from "@/services/termAdminService";
import CategoryAdminService from "@/services/categoryAdminService";
import ConfirmationModal from "@/components/common/ConfirmationModal.vue";
import TableData from "@/components/common/TableData.vue";
import BaseButton from "@/components/common/BaseButton.vue";
import BaseInput from "@/components/BaseInput.vue";
import { debounce } from "lodash";
import { useUrlQuerySync } from "@/composeables/useUrlQuerySync";

const showModalDelete = ref(false);
const isLoading = ref(false);
const itemId = ref(null);
const tableLoading = ref(true);
const searchQuery = ref("");
const currentSort = ref("newest");
const notify = inject("notify");

const route = useRoute();
const router = useRouter();

const data = ref({
  message: "",
  error: null,
  data: {
    search: "",
    page: 0,
    limit: 0,
    total_rows: 0,
    total_pages: 0,
    rows: [],
  },
});

const sortOptions = ref([
  { text: "Newest", value: "newest" },
  { text: "Oldest", value: "oldest" },
  { text: "Title A-Z", value: "title-a-z" },
  { text: "Title Z-A", value: "title-z-a" },
]);

const currentPage = ref(1);
const sortOrder = ref("desc");
const sortKey = ref("id");

const filters = ref({
  search: "",
  page: 1,
  limit: 10,
  key: "",
  sort_by: "id",
  order: "desc",
  taxonomy: "",
});

// Setup URL query sync with filters
useUrlQuerySync({
  filters,
  excludeDefaults: [
    { page: 1 },
    { limit: 10 },
    { sort_by: "id" },
    { order: "desc" },
  ],
});

const category = ref({ id: null, name: null, title: null });

// Define table headers
const headers = ref([
  { text: "Slug", value: "slug", sortable: true },
  { text: "Title", value: "title", sortable: true },
  { text: "Region", value: "locale", sortable: false },
  { text: "Priority", value: "priority", sortable: true },
]);

// Computed property to transform data for the table
const tableItems = computed(() => {
  return data.value.data.rows.map((item) => ({
    ...item,
    id: item.id,
    locale: item.locale || "-",
    priority: item.priority || "-",
  }));
});

// Sync search query with filters.search
watch(
  () => filters.value.search,
  (newValue) => {
    searchQuery.value = newValue || "";
  }
);

// Sync sort parameters with UI state and dropdown
watch(
  [() => filters.value.sort_by, () => filters.value.order],
  ([newSortBy, newOrder]) => {
    sortKey.value = newSortBy;
    sortOrder.value = newOrder;

    // Update currentSort based on filter values
    if (newSortBy === "id" && newOrder === "desc") {
      currentSort.value = "newest";
    } else if (newSortBy === "id" && newOrder === "asc") {
      currentSort.value = "oldest";
    } else if (newSortBy === "title" && newOrder === "asc") {
      currentSort.value = "title-a-z";
    } else if (newSortBy === "title" && newOrder === "desc") {
      currentSort.value = "title-z-a";
    }
  }
);

onMounted(async () => {
  filters.value.taxonomy = route.params.id;
  await getAll();
  await getCategorybyId(route.params.id);
  window.addEventListener("resize", updateLayout);
  updateLayout();
});

const handleAction = (action) => {
  if (action.type === "edit") {
    router.push(`/admin/terms/form/${route.params.id}/${action.item.id}`);
  } else if (action.type === "delete") {
    itemId.value = action.item.id;
    showModalDelete.value = true;
  } else if (action.type === "add") {
    router.push(`/admin/terms/form/${route.params.id}`);
  }
};

const getAll = async () => {
  try {
    tableLoading.value = true;

    const params = { ...filters.value };

    const response = await TermAdminService.getAll(params);
    if (response.status === 200) {
      data.value = response.data;
    }
  } catch (error) {
    console.error("Error fetching terms:", error.message);
    data.value.data.rows = [];
    data.value.data.total_pages = 0;
  } finally {
    tableLoading.value = false;
  }
};

const getCategorybyId = async (id) => {
  try {
    const response = await CategoryAdminService.getById(id);
    if (response.status === 200) {
      category.value = response.data.data;
    }
  } catch (error) {
    console.log(error.message);
  }
};

const handleSearchChange = debounce(() => {
  filters.value.search = searchQuery.value;
  filters.value.page = 1;
  currentPage.value = 1;
  getAll();
}, 300);

const handleSortChange = () => {
  switch (currentSort.value) {
    case "newest":
      filters.value.sort_by = "id";
      filters.value.order = "desc";
      break;
    case "oldest":
      filters.value.sort_by = "id";
      filters.value.order = "asc";
      break;
    case "title-a-z":
      filters.value.sort_by = "title";
      filters.value.order = "asc";
      break;
    case "title-z-a":
      filters.value.sort_by = "title";
      filters.value.order = "desc";
      break;
    default:
      filters.value.sort_by = "id";
      filters.value.order = "desc";
  }
  filters.value.page = 1;
  currentPage.value = 1;
  getAll();
};

const changePage = (page) => {
  currentPage.value = page;
  filters.value.page = page;
  getAll();
};

const handleSort = async ({ sort_by, sort_dir }) => {
  // Map sort_dir format from TableData (ASC/DESC) to our API format (asc/desc)
  const order = sort_dir.toLowerCase();

  filters.value.sort_by = sort_by;
  filters.value.order = order;

  await getAll();
};

const updateVisibilityModalDelete = () => {
  showModalDelete.value = !showModalDelete.value;
};

const remove = async (id) => {
  isLoading.value = true;
  try {
    await TermAdminService.remove(id);
    isLoading.value = false;
    showModalDelete.value = false;
    getAll();
    notify.success("Sub Category deleted successfully", "Success");
  } catch (error) {
    isLoading.value = false;
    console.error(error.message);
    notify.error("Failed to delete sub category", "Error");
  }
};

// Function to update layout based on window width
const updateLayout = () => {
  const container = document.querySelector(".admin-flex-container");
  const toolContainer = document.querySelector(".admin-list-tool-container");
  const parentContainer = document.querySelector(".border-container-unique37");
  const width = window.innerWidth;

  if (width <= 991) {
    parentContainer.insertBefore(toolContainer, container.nextSibling);
  } else {
    container.appendChild(toolContainer);
    container.insertBefore(toolContainer, container.children[1]);
  }
};

function convertTimestamp(timestamp) {
  // Buat objek Date dari timestamp
  var date = new Date(timestamp * 1000);

  // Format tanggal dan waktu
  var year = date.getFullYear();
  var month = ("0" + (date.getMonth() + 1)).slice(-2);
  var day = ("0" + date.getDate()).slice(-2);
  var hours = ("0" + date.getHours()).slice(-2);
  var minutes = ("0" + date.getMinutes()).slice(-2);
  var seconds = ("0" + date.getSeconds()).slice(-2);

  // Gabungkan hasil format
  var formattedDate =
    year +
    "-" +
    month +
    "-" +
    day +
    " " +
    hours +
    ":" +
    minutes +
    ":" +
    seconds;
  return formattedDate;
}
</script>
